import { type FormEvent, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { Button, ButtonLink, Field, FormError } from '../components/ui'
import { ApiError, api, call, messageOf } from '../lib/api'
import { meQuery } from '../lib/queries'

export const Route = createFileRoute('/reset')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: Reset,
})

function Reset() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [password, setPassword] = useState('')
  const reset = useMutation({
    mutationFn: () => call(api.POST('/auth/reset', { body: { token: token ?? '', new_password: password } })),
    onSuccess: (me) => {
      qc.clear()
      qc.setQueryData(meQuery.queryKey, me)
      navigate({ to: '/' })
    },
  })
  const dead = !token || (reset.error instanceof ApiError && reset.error.status === 410)
  if (dead) {
    return (
      <AuthLayout title="That link has expired" lede="Reset links work once and last an hour. Ask for a fresh one.">
        <ButtonLink to="/forgot" variant="primary" block>Send a new link</ButtonLink>
      </AuthLayout>
    )
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    reset.mutate()
  }
  return (
    <AuthLayout title="Choose a new password" lede="You'll be signed in here, and signed out everywhere else.">
      <form onSubmit={submit} className="grid gap-4">
        <FormError>{reset.isError && messageOf(reset.error)}</FormError>
        <Field label="New password" name="new-password" type="password" autoComplete="new-password" required minLength={10} maxLength={200} value={password} onChange={(e) => setPassword(e.target.value)} help="Ten characters or more." />
        <Button type="submit" variant="primary" block busy={reset.isPending}>Save and sign in</Button>
      </form>
    </AuthLayout>
  )
}
